import React, { useState, useEffect } from 'react';
import {
  ArrowLeft, CheckCircle, XCircle, Clock, AlertCircle,
  RefreshCw, User, Calendar, DollarSign, FileText,
  Shield, Monitor, MessageSquare, ChevronRight, Download,
} from 'lucide-react';
import { solicitudesAPI } from '../../../api/solicitudes';
import api from '../../../api/config';
import { usePermissionsContext } from '../../../contexts/PermissionsContext';

const ESTADO_CFG = {
  pendiente:   { label: 'Pendiente',   cls: 'bg-amber-100 text-amber-700',   Icon: Clock       },
  en_revision: { label: 'En revisión', cls: 'bg-blue-100 text-blue-600',     Icon: AlertCircle },
  aprobada:    { label: 'Aprobada',    cls: 'bg-green-100 text-green-700',   Icon: CheckCircle },
  en_proceso:  { label: 'En proceso',  cls: 'bg-purple-100 text-purple-700', Icon: RefreshCw   },
  completada:  { label: 'Completada',  cls: 'bg-teal-100 text-teal-700',     Icon: CheckCircle },
  rechazada:   { label: 'Rechazada',   cls: 'bg-red-100 text-red-600',       Icon: XCircle     },
  cancelada:   { label: 'Cancelada',   cls: 'bg-gray-100 text-gray-500',     Icon: XCircle     },
};

const fmt = (n) =>
  parseFloat(n || 0).toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtDate = (d) => {
  if (!d) return '—';
  const s = String(d).split('T')[0];
  return new Date(s + 'T12:00:00').toLocaleDateString('es-DO', { day: '2-digit', month: 'long', year: 'numeric' });
};

function EstadoBadge({ estado }) {
  const cfg = ESTADO_CFG[estado] || ESTADO_CFG.pendiente;
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium ${cfg.cls}`}>
      <cfg.Icon size={13} /> {cfg.label}
    </span>
  );
}

function Campo({ label, value }) {
  if (!value && value !== 0) return null;
  return (
    <div>
      <p className="text-xs text-gray-400 mb-0.5">{label}</p>
      <p className="text-sm text-gray-900">{value}</p>
    </div>
  );
}

function DetallesEspecificos({ tipo_codigo, detalles, solicitud }) {
  if (!detalles) return null;
  const d = detalles;

  if (tipo_codigo?.startsWith('CERT_')) {
    return (
      <div className="grid grid-cols-2 gap-4">
        <Campo label="Entidad destino" value={d.entidad_destino} />
        <Campo label="Incluir salario" value={d.incluir_salario ? 'Sí' : 'No'} />
      </div>
    );
  }
  if (tipo_codigo?.startsWith('PRES_')) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <Campo label="Monto solicitado"  value={`RD$ ${fmt(d.monto_solicitado || solicitud.monto_solicitado)}`} />
        <Campo label="Plazo"             value={`${d.plazo_meses || solicitud.plazo_meses} meses`} />
        <Campo label="Tasa de interés"   value={d.tasa_interes ? `${d.tasa_interes}%` : '0%'} />
        <Campo label="Quincena descuento" value={d.quincena_descuento ? (d.quincena_descuento.startsWith('Q') ? d.quincena_descuento : `Q${d.quincena_descuento}`) : '—'} />
        {d.prestamo_id && <Campo label="Préstamo creado" value={`ID: ${d.prestamo_id.slice(0,8)}…`} />}
      </div>
    );
  }
  if (tipo_codigo?.startsWith('VAC_') || tipo_codigo?.startsWith('NOV_')) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <Campo label="Fecha inicio"     value={fmtDate(d.fecha_inicio || solicitud.fecha_inicio)} />
        <Campo label="Fecha fin"        value={fmtDate(d.fecha_fin || solicitud.fecha_fin)} />
        <Campo label="Fecha retorno"    value={fmtDate(d.fecha_retorno || solicitud.fecha_retorno)} />
        <Campo label="Días solicitados" value={d.dias_solicitados} />
        {d.nombre_medico    && <Campo label="Médico"     value={d.nombre_medico} />}
        {d.diagnostico      && <Campo label="Diagnóstico" value={d.diagnostico} />}
        {d.parentesco       && <Campo label="Parentesco"  value={d.parentesco} />}
        {d.nombre_fallecido && <Campo label="Fallecido"   value={d.nombre_fallecido} />}
      </div>
    );
  }
  if (tipo_codigo === 'DISC_AMONESTACION') {
    return (
      <div className="grid grid-cols-2 gap-4">
        <Campo label="Tipo"            value={d.tipo_amonestacion} />
        <Campo label="Gravedad"        value={d.gravedad} />
        <Campo label="Fecha incidente" value={fmtDate(d.fecha_incidente)} />
        <Campo label="Lugar"           value={d.lugar_incidente} />
        <Campo label="Testigos"        value={d.testigos} />
        <Campo label="Días suspensión" value={d.dias_suspension} />
        {d.motivo && (
          <div className="col-span-2">
            <p className="text-xs text-gray-400 mb-0.5">Motivo</p>
            <p className="text-sm text-gray-900">{d.motivo}</p>
          </div>
        )}
        {d.medida_correctiva && (
          <div className="col-span-2">
            <p className="text-xs text-gray-400 mb-0.5">Medida correctiva</p>
            <p className="text-sm text-gray-900">{d.medida_correctiva}</p>
          </div>
        )}
      </div>
    );
  }
  if (tipo_codigo?.startsWith('SEG_')) {
    return (
      <div className="grid grid-cols-2 gap-4">
        <Campo label="Nombre dependiente" value={d.nombre_dependiente} />
        <Campo label="Parentesco"         value={d.parentesco || d.tipo_dependiente} />
        <Campo label="Cédula"             value={d.cedula_dependiente} />
        <Campo label="Fecha nacimiento"   value={fmtDate(d.fecha_nacimiento_dependiente)} />
      </div>
    );
  }
  if (tipo_codigo?.startsWith('SIS_')) {
    return (
      <div className="grid grid-cols-2 gap-4">
        <Campo label="Sistema / Equipo" value={d.sistema_nombre || d.tipo_equipo} />
        <Campo label="Especificaciones"  value={d.especificaciones} />
        {d.equipo_asignado && <Campo label="Equipo asignado" value={d.equipo_asignado} />}
        {d.serial_equipo   && <Campo label="Serial"          value={d.serial_equipo} />}
      </div>
    );
  }
  return null;
}

export default function DetalleSolicitud({ solicitudId, currentCompany, currentUser, onBack, onRefresh }) {
  const [data, setData]           = useState(null);
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState('');
  const [motivoRechazo, setMotivoRechazo] = useState('');
  const [showRechazoForm, setShowRechazoForm] = useState(false);
  const [comentarios, setComentarios] = useState('');
  const [cambiandoEstado, setCambiandoEstado] = useState(false);

  const { can } = usePermissionsContext();
  const isRRHH = can('REQUEST_APPROVE');

  useEffect(() => {
    solicitudesAPI.getById(solicitudId)
      .then(r => setData(r))
      .catch(() => setError('Error al cargar la solicitud'))
      .finally(() => setLoading(false));
  }, [solicitudId]);

  const handleAprobar = async () => {
    if (!window.confirm('¿Confirmar aprobación de esta solicitud?')) return;
    setSaving(true);
    try {
      await solicitudesAPI.aprobar(solicitudId, { comentarios_rrhh: comentarios || undefined });
      onRefresh();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al aprobar');
      setSaving(false);
    }
  };

  const handleRechazar = async () => {
    if (!motivoRechazo.trim()) { setError('Ingresa el motivo de rechazo'); return; }
    setSaving(true);
    try {
      await solicitudesAPI.rechazar(solicitudId, { motivo_rechazo: motivoRechazo, comentarios_rrhh: comentarios || undefined });
      onRefresh();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al rechazar');
      setSaving(false);
    }
  };

  const handleCompletar = async () => {
    if (!window.confirm('¿Marcar esta solicitud como completada?')) return;
    setSaving(true);
    try {
      await solicitudesAPI.completar(solicitudId, { comentarios_rrhh: comentarios || undefined });
      onRefresh();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al completar');
      setSaving(false);
    }
  };

  const handleCambiarEstado = async (nuevoEstado, needsConfirm = false) => {
    if (nuevoEstado === solicitud?.estado) return;
    if (needsConfirm) {
      if (!window.confirm('Al aprobar esta solicitud se creará el registro correspondiente en el módulo relacionado. ¿Confirmar?')) return;
    }
    setCambiandoEstado(true);
    setError('');
    try {
      await solicitudesAPI.cambiarEstado(solicitudId, nuevoEstado, comentarios || undefined);
      onRefresh();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al cambiar estado');
      setCambiandoEstado(false);
    }
  };

  const handleCancelar = async () => {
    if (!window.confirm('¿Cancelar esta solicitud?')) return;
    setSaving(true);
    try {
      await solicitudesAPI.cancelar(solicitudId);
      onRefresh();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al cancelar');
      setSaving(false);
    }
  };

  const downloadDocumento = async (documentId, nombre) => {
    try {
      const response = await api.get(`/employee-documents/${documentId}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', nombre || 'documento.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Error al descargar el documento');
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-24">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600" />
    </div>
  );

  if (error && !data) return (
    <div className="p-6 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">{error}</div>
  );

  const { solicitud, historial = [], adjuntos = [] } = data || {};
  const canApprove    = false; // reemplazado por botones de estado manual
  const canComplete   = isRRHH && solicitud?.estado === 'en_proceso';
  const canCancel     = ['pendiente', 'en_revision'].includes(solicitud?.estado);
  const isFinal        = ['rechazada', 'cancelada', 'declinada'].includes(solicitud?.estado);
  const canChangeState = isRRHH && !isFinal;

  // Tipos que tienen handler: al aprobarlos se crea un registro en otro módulo
  const HAS_HANDLER_PREFIXES = ['VAC_', 'NOV_', 'PRES_', 'CERT_', 'SEG_', 'DISC_'];
  const tieneHandler = HAS_HANDLER_PREFIXES.some(p => solicitud?.tipo_codigo?.startsWith(p));

  const MANUAL_ESTADO_OPTIONS = [
    { value: 'pendiente',   label: 'Pendiente'   },
    { value: 'en_revision', label: 'En revisión' },
    { value: 'aprobada',    label: 'Aprobar',     confirm: tieneHandler },
    { value: 'en_proceso',  label: 'En proceso'  },
    // completada solo para tipos sin handler (IT, quejas) o ya aprobados
    ...(!tieneHandler || solicitud?.estado === 'aprobada' || solicitud?.estado === 'en_proceso'
      ? [{ value: 'completada', label: 'Completada' }]
      : []),
  ];

  return (
    <div className="space-y-4">

      {/* Back + header */}
      <div className="flex items-center gap-3">
        <button
          onClick={onBack}
          className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
        >
          <ArrowLeft size={14} className="text-gray-500" />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h1 className="text-base font-bold text-gray-900">{solicitud?.asunto}</h1>
            <EstadoBadge estado={solicitud?.estado} />
          </div>
          <p className="text-xs text-gray-400 mt-0.5">
            {solicitud?.tipo_nombre} · {solicitud?.categoria}
          </p>
        </div>
      </div>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

        {/* Left: main info */}
        <div className="lg:col-span-2 space-y-4">

          {/* Datos empleado */}
          <div className="bg-white border border-gray-200 rounded-xl p-4">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Empleado</h3>
            <div className="flex items-center gap-3">
              {solicitud?.empleado_foto ? (
                <img src={solicitud.empleado_foto} alt="" className="w-10 h-10 rounded-full object-cover" />
              ) : (
                <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                  <span className="text-sm font-bold text-purple-600">
                    {solicitud?.empleado_nombre?.[0]?.toUpperCase()}
                  </span>
                </div>
              )}
              <div>
                <p className="font-semibold text-gray-900">{solicitud?.empleado_nombre}</p>
                <p className="text-xs text-gray-400">{solicitud?.empleado_codigo} · {solicitud?.departamento} · {solicitud?.cargo}</p>
              </div>
            </div>
          </div>

          {/* Detalles de la solicitud */}
          <div className="bg-white border border-gray-200 rounded-xl p-4">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Detalle de la Solicitud</h3>
            <div className="space-y-4">
              {solicitud?.descripcion && (
                <div>
                  <p className="text-xs text-gray-400 mb-0.5">Descripción</p>
                  <p className="text-sm text-gray-900">{solicitud.descripcion}</p>
                </div>
              )}
              {solicitud?.comentarios_empleado && (
                <div>
                  <p className="text-xs text-gray-400 mb-0.5">Comentarios del empleado</p>
                  <p className="text-sm text-gray-900">{solicitud.comentarios_empleado}</p>
                </div>
              )}
              <DetallesEspecificos
                tipo_codigo={solicitud?.tipo_codigo}
                detalles={solicitud?.detalles}
                solicitud={solicitud}
              />
            </div>
          </div>

          {/* Respuesta RRHH */}
          {solicitud?.comentarios_rrhh && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <h3 className="text-xs font-semibold text-blue-600 uppercase tracking-wider mb-2">Respuesta RRHH</h3>
              <p className="text-sm text-gray-800">{solicitud.comentarios_rrhh}</p>
              {solicitud.aprobada_por && (
                <p className="text-xs text-blue-500 mt-2">Por: {solicitud.aprobada_por} · {fmtDate(solicitud.fecha_aprobacion)}</p>
              )}
            </div>
          )}

          {/* Motivo rechazo */}
          {solicitud?.motivo_rechazo && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4">
              <h3 className="text-xs font-semibold text-red-600 uppercase tracking-wider mb-2">Motivo de Rechazo</h3>
              <p className="text-sm text-gray-800">{solicitud.motivo_rechazo}</p>
            </div>
          )}

          {/* Documento generado (CERT_*) */}
          {solicitud?.tipo_codigo?.startsWith('CERT_') &&
           solicitud?.estado === 'aprobada' &&
           solicitud?.detalles?.document_id && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-4">
              <h3 className="text-xs font-semibold text-green-700 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                <FileText size={13} /> Documento Generado
              </h3>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-900">{solicitud.asunto}</p>
                  <p className="text-xs text-gray-500 mt-0.5">Generado al aprobar la solicitud</p>
                </div>
                <button
                  onClick={() => downloadDocumento(solicitud.detalles.document_id, solicitud.asunto + '.pdf')}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white text-xs font-semibold rounded-lg hover:bg-green-700 transition-colors"
                >
                  <Download size={12} /> Descargar PDF
                </button>
              </div>
            </div>
          )}

          {/* Adjuntos */}
          {adjuntos.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Archivos Adjuntos</h3>
              <div className="space-y-2">
                {adjuntos.map(a => (
                  <div key={a.id} className="flex items-center gap-2 p-2 border border-gray-100 rounded-lg">
                    <FileText size={14} className="text-gray-400" />
                    <span className="text-sm text-gray-700 flex-1">{a.nombre_archivo}</span>
                    {a.descripcion && <span className="text-xs text-gray-400">{a.descripcion}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right: historial + acciones */}
        <div className="space-y-4">

          {/* Info rápida */}
          <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-3">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Información</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Prioridad</span>
                <span className="font-medium text-gray-900 capitalize">{solicitud?.prioridad}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Fecha</span>
                <span className="font-medium text-gray-900">{fmtDate(solicitud?.fecha_solicitud)}</span>
              </div>
              {solicitud?.fecha_inicio && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Desde</span>
                  <span className="font-medium text-gray-900">{fmtDate(solicitud.fecha_inicio)}</span>
                </div>
              )}
              {solicitud?.fecha_fin && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Hasta</span>
                  <span className="font-medium text-gray-900">{fmtDate(solicitud.fecha_fin)}</span>
                </div>
              )}
              {solicitud?.monto_solicitado && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Monto</span>
                  <span className="font-semibold text-gray-900">RD$ {fmt(solicitud.monto_solicitado)}</span>
                </div>
              )}
            </div>
          </div>

          {/* Acciones RRHH */}
          {(canApprove || canComplete || canCancel || canChangeState) && (
            <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-3">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Acciones</h3>

              <textarea
                placeholder="Comentarios (opcional)..."
                value={comentarios}
                onChange={e => setComentarios(e.target.value)}
                rows={2}
                className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 resize-none focus:outline-none focus:border-purple-400"
              />

              {/* Cambio manual de estado */}
              {canChangeState && !isFinal && (
                <div>
                  <p className="text-xs text-gray-500 mb-1.5">Cambiar estado</p>
                  <div className="flex gap-1.5 flex-wrap">
                    {MANUAL_ESTADO_OPTIONS.map(opt => {
                      const isCurrent = solicitud?.estado === opt.value;
                      return (
                        <button
                          key={opt.value}
                          onClick={() => handleCambiarEstado(opt.value, opt.confirm)}
                          disabled={isCurrent || cambiandoEstado}
                          className={`px-3 py-1.5 text-xs rounded-lg border font-medium transition-colors ${
                            isCurrent
                              ? 'bg-purple-600 text-white border-purple-600 cursor-default'
                              : 'border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40'
                          }`}
                        >
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {canApprove && !showRechazoForm && (
                <div className="space-y-2">
                  <button
                    onClick={handleAprobar}
                    disabled={saving}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-green-600 text-white text-sm font-semibold rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
                  >
                    <CheckCircle size={14} /> Aprobar
                  </button>
                  <button
                    onClick={() => setShowRechazoForm(true)}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 border border-red-200 text-red-600 text-sm font-medium rounded-lg hover:bg-red-50 transition-colors"
                  >
                    <XCircle size={14} /> Rechazar
                  </button>
                </div>
              )}

              {showRechazoForm && (
                <div className="space-y-2">
                  <textarea
                    placeholder="Motivo de rechazo (requerido)..."
                    value={motivoRechazo}
                    onChange={e => setMotivoRechazo(e.target.value)}
                    rows={3}
                    className="w-full text-sm border border-red-200 rounded-lg px-3 py-2 resize-none focus:outline-none focus:border-red-400"
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={handleRechazar}
                      disabled={saving}
                      className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-red-600 text-white text-sm font-semibold rounded-lg hover:bg-red-700 disabled:opacity-50"
                    >
                      <XCircle size={13} /> Confirmar rechazo
                    </button>
                    <button
                      onClick={() => setShowRechazoForm(false)}
                      className="px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50"
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              )}

              {canComplete && (
                <button
                  onClick={handleCompletar}
                  disabled={saving}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-teal-600 text-white text-sm font-semibold rounded-lg hover:bg-teal-700 disabled:opacity-50 transition-colors"
                >
                  <CheckCircle size={14} /> Marcar completada
                </button>
              )}

              {canCancel && (
                <button
                  onClick={handleCancelar}
                  disabled={saving}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 border border-gray-200 text-gray-600 text-sm rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
                >
                  Cancelar solicitud
                </button>
              )}
            </div>
          )}

          {/* Historial */}
          <div className="bg-white border border-gray-200 rounded-xl p-4">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Historial</h3>
            {historial.length === 0 ? (
              <p className="text-xs text-gray-400">Sin historial</p>
            ) : (
              <div className="space-y-3">
                {historial.map((h, i) => (
                  <div key={h.id} className="flex gap-2">
                    <div className="flex flex-col items-center">
                      <div className="w-2 h-2 rounded-full bg-purple-400 mt-1.5 flex-shrink-0" />
                      {i < historial.length - 1 && (
                        <div className="w-0.5 flex-1 bg-gray-100 mt-1" />
                      )}
                    </div>
                    <div className="pb-3">
                      <p className="text-xs font-medium text-gray-900">
                        {h.estado_anterior
                          ? `${h.estado_anterior} → ${h.estado_nuevo}`
                          : h.estado_nuevo}
                      </p>
                      {h.comentario && (
                        <p className="text-xs text-gray-500 mt-0.5">{h.comentario}</p>
                      )}
                      <p className="text-xs text-gray-400 mt-0.5">
                        {h.usuario} · {new Date(h.created_at).toLocaleDateString('es-DO', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
}
